/**
 * Wall crusher BODY — the plated armor of each crusher wall. *Core decides, Phaser
 * renders*: core owns the wall rect + its creep→crush→open motion; this fills it with
 * a TILED {@link HAZARD_TEX.wallPanel} so the body reads as crisp, high-detail armor at
 * ANY wall size (a {@link Phaser.GameObjects.TileSprite} repeats the 128px panel rather
 * than stretching one low-res box). The spiked crushing face is drawn on top by
 * {@link import('./WallSpikeFx').WallSpikeFx}.
 *
 * Phase-aware to match the telegraph: dim + teal while the wall is harmlessly CREEPING
 * in, full once it goes lethal. One pooled TileSprite per wall id.
 */
import Phaser from 'phaser';
import type { RectHazard } from '../core';
import { HAZARD_TEX, HAZARD_COLORS } from './textures';

/** Hazard render layer (under the spikes at 6 + the player at 10). */
const BODY_DEPTH = 5;
/** Author-time panel size (matches textures.ts) — the tile size in canonical px. */
const PANEL = 128;

export class WallBodyFx {
  private readonly scene: Phaser.Scene;
  private readonly free: Phaser.GameObjects.TileSprite[] = [];
  private readonly all: Phaser.GameObjects.TileSprite[] = [];
  private readonly state = new Map<number, Phaser.GameObjects.TileSprite>();

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /** Position + size one wall's tiled body for the current frame. `phase` < 1 = creep. */
  update(h: RectHazard, phase: number): void {
    let t = this.state.get(h.id);
    if (!t) {
      t = this.acquire();
      this.state.set(h.id, t);
    }
    t.setPosition(h.x, h.y).setSize(h.w, h.h).setVisible(true);
    if (phase < 1) {
      // Harmless creep: teal-washed + dim, ramping toward solid like the legacy telegraph.
      t.setTint(HAZARD_COLORS.turretBody).setAlpha(0.18 + 0.37 * phase);
    } else {
      t.clearTint().setAlpha(1);
    }
  }

  /** Release the body for any wall id no longer live this step. */
  sweep(alive: Set<number>): void {
    for (const [id, t] of this.state) {
      if (alive.has(id)) continue;
      this.release(t);
      this.state.delete(id);
    }
  }

  reset(): void {
    for (const t of this.state.values()) this.release(t);
    this.state.clear();
  }

  destroy(): void {
    this.state.clear();
    for (const t of this.all) t.destroy();
    this.all.length = 0;
    this.free.length = 0;
  }

  private acquire(): Phaser.GameObjects.TileSprite {
    const t = this.free.pop() ?? this.make();
    t.setActive(true).setVisible(true);
    return t;
  }
  private release(t: Phaser.GameObjects.TileSprite): void {
    t.setActive(false).setVisible(false).clearTint().setAlpha(1);
    this.free.push(t);
  }
  private make(): Phaser.GameObjects.TileSprite {
    return this.scene.add
      .tileSprite(0, 0, PANEL, PANEL, HAZARD_TEX.wallPanel)
      .setOrigin(0, 0)
      .setDepth(BODY_DEPTH);
  }
}
